/**
 * Offline-friendly fuzzy search. The API uses Postgres trigram search; the app uses this
 * on its local content bundle so search works in the woods with no signal.
 */

export function normalize(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Padded character trigrams, matching Postgres pg_trgm's approach. */
export function trigrams(text: string): Set<string> {
  const out = new Set<string>();
  for (const word of normalize(text).split(" ")) {
    if (!word) continue;
    const padded = `  ${word} `;
    for (let i = 0; i < padded.length - 2; i++) out.add(padded.slice(i, i + 3));
  }
  return out;
}

/** Jaccard similarity of trigram sets, 0..1. */
export function similarity(a: string, b: string): number {
  const ta = trigrams(a);
  const tb = trigrams(b);
  if (ta.size === 0 || tb.size === 0) return 0;
  let shared = 0;
  for (const t of ta) if (tb.has(t)) shared++;
  return shared / (ta.size + tb.size - shared);
}

export interface Searchable {
  name: string;
  /** Secondary text: other names, intents, keywords. */
  aliases?: string[];
}

/** Score how well `item` matches `query` (0 = no match). */
export function scoreMatch(item: Searchable, query: string): number {
  const q = normalize(query);
  if (!q) return 0;
  const name = normalize(item.name);
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  if (name.split(" ").some((w) => w.startsWith(q))) return 60;
  if (name.includes(q)) return 50;
  const aliases = (item.aliases ?? []).map(normalize);
  if (aliases.some((a) => a === q || a.split(" ").some((w) => w.startsWith(q)))) return 40;
  const fuzzy = Math.max(similarity(name, q), ...aliases.map((a) => similarity(a, q)));
  return fuzzy >= 0.3 ? Math.round(fuzzy * 30) : 0;
}

/** Rank items by match score (best first), dropping non-matches. */
export function searchItems<T extends Searchable>(items: T[], query: string, limit = 50): T[] {
  return items
    .map((item) => ({ item, score: scoreMatch(item, query) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.item.name.localeCompare(b.item.name))
    .slice(0, limit)
    .map((x) => x.item);
}
